/* Contact page — interactive Google Map for the contact_map section.
   Progressive enhancement: the static map image stays visible until the map loads. */
(() => {
  const SELECTOR = {
    map: '[data-contact-map]',
    media: '.contact-map__map',
    card: '.contact-map__map-card',
  };

  const GEO_CACHE_KEY = 'contact_map_geo:';
  const MOBILE_MEDIA = '(max-width: 768px)';

  /* Размеры пина и зазор до плашки — из макета (desktop 52319-211996) */
  const PIN = { width: 32, height: 38 };
  const PIN_MOBILE = { width: 22, height: 26 };
  const CARD_GAP = 7;
  const CARD_OFFSET_X = 4;

  const PIN_ICON =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38">' +
        '<path fill="#0AA03C" d="M16 0C7.16 0 0 7.16 0 16c0 10.5 13.1 20.5 15.06 21.6a1.6 1.6 0 0 0 1.88 0C18.9 36.5 32 26.5 32 16 32 7.16 24.84 0 16 0Z"/>' +
        '<circle cx="16" cy="15.5" r="6" fill="#067426"/>' +
        '</svg>'
    );

  let mapsApiPromise = null;

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) return Promise.resolve();
    if (mapsApiPromise) return mapsApiPromise;

    mapsApiPromise = new Promise((resolve, reject) => {
      window.__contactMapApiReady = resolve;
      // Google вызывает этот глобальный колбэк при проблемах с ключом
      // (RefererNotAllowed, InvalidKey и т.п.) — прячем канвас, чтобы вместо
      // серой ошибки Google осталась статичная картинка-фолбэк.
      window.gm_authFailure = () => {
        document.querySelectorAll(SELECTOR.map).forEach((el) => {
          el.style.display = 'none';
        });
      };
      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&loading=async&language=en&region=GB&callback=__contactMapApiReady';
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return mapsApiPromise;
  }

  function geocode(address) {
    const cacheKey = GEO_CACHE_KEY + address;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && typeof cached.lat === 'number') return Promise.resolve(cached);
    } catch {
      /* игнорируем битый кэш */
    }

    return new Promise((resolve, reject) => {
      new google.maps.Geocoder().geocode({ address }, (results, status) => {
        if (status !== 'OK' || !results || !results[0]) {
          reject(new Error('Geocode failed: ' + status));
          return;
        }
        const location = results[0].geometry.location;
        const coords = { lat: location.lat(), lng: location.lng() };
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(coords));
        } catch {
          /* sessionStorage может быть недоступен */
        }
        resolve(coords);
      });
    });
  }

  function parsePan(value) {
    const [x, y] = (value || '').split(',').map(Number);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }

  /* Прибиваем плашку «We are here!» к координатам офиса, чтобы она ездила
     вместе с картой при перетаскивании и зуме. */
  function attachCardOverlay(map, position, card) {
    const overlay = new google.maps.OverlayView();

    overlay.onAdd = function () {
      card.classList.add('contact-map__map-card--anchored');
      this.getPanes().floatPane.appendChild(card);
    };

    overlay.draw = function () {
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(position);
      if (!point) return;
      card.style.left = Math.round(point.x - CARD_OFFSET_X) + 'px';
      card.style.top = Math.round(point.y - PIN.height - CARD_GAP - card.offsetHeight) + 'px';
    };

    overlay.onRemove = function () {
      if (card.parentNode) card.parentNode.removeChild(card);
    };

    overlay.setMap(map);
  }

  async function initMap(root) {
    if (root.dataset.contactMapInit === 'true') return;
    root.dataset.contactMapInit = 'true';

    const apiKey = root.dataset.apiKey;
    const address = root.dataset.address;
    if (!apiKey || !address) return;

    try {
      await loadGoogleMaps(apiKey);

      // Явные координаты из настроек избавляют от запроса к Geocoding API
      const lat = parseFloat(root.dataset.lat);
      const lng = parseFloat(root.dataset.lng);
      const coords = Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : await geocode(address);

      const isMobile = window.matchMedia(MOBILE_MEDIA).matches;

      const position = new google.maps.LatLng(coords.lat, coords.lng);
      const map = new google.maps.Map(root, {
        center: position,
        zoom: parseInt(root.dataset.zoom, 10) || 16,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
      });

      if (root.dataset.showPin === 'true') {
        const pin = isMobile ? PIN_MOBILE : PIN;
        new google.maps.Marker({
          position,
          map,
          title: address,
          icon: {
            url: PIN_ICON,
            scaledSize: new google.maps.Size(pin.width, pin.height),
            anchor: new google.maps.Point(pin.width / 2, pin.height),
          },
        });
      }

      // Сдвигаем центр, чтобы пин встал в ту же точку композиции, что в макете
      // (оверлей-карточка занимает центр карты).
      const pan = parsePan(isMobile ? root.dataset.panMobile : root.dataset.panDesktop);
      if (pan) {
        google.maps.event.addListenerOnce(map, 'idle', () => map.panBy(pan.x, pan.y));
      }

      // На мобиле плашка по макету прибита к низу карты, поэтому к координатам
      // её привязываем только на десктопе.
      const media = root.closest(SELECTOR.media);
      const card = media && media.querySelector(SELECTOR.card);
      if (card && !isMobile) attachCardOverlay(map, position, card);
    } catch (error) {
      // Карта не загрузилась — остаётся статичная картинка-фолбэк
      console.error('[contact-map]', error);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR.map).forEach(initMap);
  });
})();
